import reviewApi from '@/api/review'

const state = () => ({
  all: [],
  pendingReviews: [],
  selectedReviews: [],
  review: {},
  replyReview: null,
  reviewUpdate: null,
  reviewApprove: null,
  reviewDisapprove: null,
  reviewIgnore: null,
  replyDeleted: null,
  reportData: null,
  reportExport: null,
  reviewsExport: null,
  stats: {},
  chartData: {},
  words: [],
})

const getters = {}

const actions = {
  async getReviews ({ commit }, params) {
    const result = await reviewApi.getAll(params)
    commit('setReviews', result.data)
  },
  async approveBulk({ state, commit, dispatch }, { router }) {
    if (!state.selectedReviews.length) {
        console.warn("⚠️ No selected reviews. Aborting request.");
        return;
    }
    const account = state.pendingReviews.data.length > 0 ? state.pendingReviews.data[0].account.name : null;
    const payload = {
        params: {
            ids: state.selectedReviews,
            account: account,
            allSelected: false
        }
    };

    if (!payload.params.account) {
        console.error("Account is missing in payload! Check Vuex state.");
        return;
    }

    try {
        const response = await reviewApi.approveBulk(payload);
        const taskId = response.data?.tasks?.[0]?.id;

        if (taskId) {
            router.push(`/task/${taskId}`); 
        } else {
            console.warn("No Task ID found in API response.");
        }

        dispatch('getPendingReviews'); 
        commit('clearSelectedReviews');
    } catch (error) {
        console.error("Error in approveBulk:", error.response?.data || error);
    }
  },

  async getWords({ commit }) {
    try {
      const response = await reviewApi.getWords();
      commit('setWords', response.data); 
      return response.data;
    } catch (error) {
      console.error('Error fetching words:', error);
    }
  },  

  async disapproveBulk({ state, commit, dispatch }, { router }) {
    if (!state.selectedReviews.length) {
        console.warn("⚠️ No selected reviews. Aborting request.");
        return;
    }
    const account = state.pendingReviews.data.length > 0 ? state.pendingReviews.data[0].account.name : null;
    const payload = {
        params: {
            ids: state.selectedReviews,
            account: account,
            allSelected: false
        }
    };

    if (!payload.params.account) {
        console.error("Account is missing in payload! Check Vuex state.");
        return;
    }

    try {
        const response = await reviewApi.disapproveBulk(payload);
        const taskId = response.data?.tasks?.[0]?.id;

        if (taskId) {
            router.push(`/task/${taskId}`);
        } else {
            console.warn("No Task ID found in API response.");
        }

        dispatch('getPendingReviews'); 
        commit('clearSelectedReviews');
    } catch (error) {
        console.error("Error in disapproveBulk:", error.response?.data || error);
    }
  },
  
  async getPendingReviews ({ commit }, params) {
    const result = await reviewApi.getPendingReviews(params)
    commit('setPendingReviews', result.data)
  },

  async getStats ({ commit }, params) {
    const result = await reviewApi.getStats(params)
    commit('setStats', result.data)
  },

  async getChartData ({ commit }, params) {
    const result = await reviewApi.getChartData(params)
    commit('setChartData', result.data)
  },

  async getReview ({ commit }, id) {
    const result = await reviewApi.getOne(id)
    commit('setReview', result.data)
  },

  async reply ({ commit }, { id, data }) {
    const result = await reviewApi.reply(id, data)
    commit('setReplyReview', result.data)
  },

  async update ({ commit }, { id, data }) {
    const result = await reviewApi.update(id, data)
    commit('setReviewUpdate', result.data)
  },

  async approve ({ commit }, id) {
    const result = await reviewApi.approve(id)
    commit('setReviewApprove', result.data)
  },

  async disapprove ({ commit }, id) {
    const result = await reviewApi.disapprove(id)
    commit('setReviewDisapprove', result.data)
  },

  async ignore ({ commit }, id) {
    const result = await reviewApi.ignore(id)
    commit('setReviewIgnore', result.data)
  },

  async deleteReply ({ commit }, { id }) {
    const result = await reviewApi.deleteReply(id)
    commit('setReplyDeleted', result.data)
  },

  async getReportData ({ commit }) {
    const result = await reviewApi.getReportData()
    commit('setReportData', result.data)
  },

  async exportReport ({ commit }, params) {
    const result = await reviewApi.exportReport(params)
    commit('setReportExport', result.data)
  },

  async exportReviews({ commit }, params) {
    const result = await reviewApi.exportReviews(params)
    commit('setReviewsExport', result.data)
  },
}

const mutations = {
  setSelectedReviews(state, reviews) {
    state.selectedReviews = reviews;
  },
  clearSelectedReviews(state) {
    state.selectedReviews = [];
  },
  setReview (state, review) {
    state.review = review
  },
  setReviews (state, reviews) {
    state.all = reviews
  },
  setPendingReviews (state, reviews) {
    state.pendingReviews = reviews
  },
  setReplyReview (state, result) {
    state.replyReview = result
  },
  setReviewUpdate (state, result) {
    state.reviewUpdate = result
  },
  setReviewApprove (state, result) {
    state.reviewApprove = result
  },
  setReviewDisapprove (state, result) {
    state.reviewDisapprove = result
  },
  setReviewIgnore (state, result) {
    state.reviewIgnore = result
  },
  setReplyDeleted (state, result) {
    state.replyDeleted = result
  },
  setReportData (state, result) {
    state.reportData = result
  },
  setReportExport (state, result) {
    state.reportExport = result
  },
  setReviewsExport (state, result) {
    state.reviewsExport = result
  },
  setStats (state, stats) {
    state.stats = stats
  },
  setChartData (state, data) {
    state.chartData = data
  },
  setWords(state, words) {
    state.words = words;
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}